import React, { useEffect, useRef, useState } from "react";
import { api, apiError, API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Printer, ChevronRight, ChevronDown, ChevronUp, Phone, Radar, PenLine, Truck, Users, Clock, RefreshCw, GripVertical, UserPlus, AlertTriangle, Volume2, VolumeX, ListChecks, Wand2, ChevronsDownUp, ChevronsUpDown, Ruler } from "lucide-react";
import { toast } from "sonner";
import { STATUS_META, STATUS_FLOW, today } from "@/lib/constants";
import { FleetMap } from "@/components/FleetMap";
import { EntityLink } from "@/components/EntityLink";
import { useLiveSync } from "@/hooks/useLiveSync";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { ChatPanel } from "@/components/ChatPanel";
import { AssignSheet } from "@/components/AssignSheet";

export default function Laregul() {
  const [date, setDate] = useState(today());
  const [rides, setRides] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [showMap, setShowMap] = useState(false);
  const [view, setView] = useState("vehicle"); // vehicle | driver | timeline
  const [positions, setPositions] = useState([]);
  const [driverPos, setDriverPos] = useState([]);
  const [showDrivers, setShowDrivers] = useState(true);
  const [trackDriver, setTrackDriver] = useState("");
  const [track, setTrack] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const refreshTimers = useRef([]);

  // --- Glisser-déposer : réaffectation dans le planning + création depuis le bac patients ---
  const [dragging, setDragging] = useState(false);
  const [hoverZone, setHoverZone] = useState(null); // id véhicule | "unassigned"
  const [patientPanel, setPatientPanel] = useState(false);
  const [patients, setPatients] = useState([]);
  const [pq, setPq] = useState("");
  const [createDlg, setCreateDlg] = useState(null); // {patient, vehicleId, vehicleLabel}
  const [nTime, setNTime] = useState("");
  const [nVt, setNVt] = useState("VSL");
  const [destQuery, setDestQuery] = useState("");
  const [destAddr, setDestAddr] = useState(null);
  const [creating, setCreating] = useState(false);

  // --- Affectation assistée : sélection multiple, panneau d'affectation, panneaux repliables ---
  const [selected, setSelected] = useState([]);
  const [sheet, setSheet] = useState(null); // {rideIds:[], refRideId}
  const [collapsed, setCollapsed] = useState([]);
  const isCollapsed = (k) => collapsed.includes(k);
  const toggleCollapse = (k) => setCollapsed((c) => (c.includes(k) ? c.filter((x) => x !== k) : [...c, k]));
  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const openSheet = (ids) => setSheet({ rideIds: ids, refRideId: ids[0] });
  const collapseAll = (keys) => setCollapsed(keys);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);
  const recomputeGeo = async () => {
    setGeoBusy(true);
    try {
      const { data } = await api.post("/rides/recompute-geo", { date });
      if (!data.corrected.length && !data.failed.length) {
        toast.success(`${data.checked} course(s) vérifiée(s) — toutes les distances sont correctes`);
      } else {
        data.corrected.slice(0, 4).forEach((c) => {
          toast.warning(`${c.time} · ${c.patient_name} : ${c.before_km} km → ${c.after_km} km`);
        });
        if (data.corrected.length) toast.success(`${data.corrected.length} distance(s) corrigée(s) sur ${data.checked} course(s)`);
        if (data.failed.length) toast.error(`${data.failed.length} adresse(s) non géolocalisable(s) — à corriger manuellement`);
        load();
      }
    } catch (err) { toast.error(apiError(err)); }
    finally { setGeoBusy(false); }
  };
  const autoOne = async (rid) => {
    try {
      const { data } = await api.post(`/rides/${rid}/auto-assign`);
      toast.success(`Affectée à ${data.chosen.label}`);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const bulkAuto = async () => {
    setBulkBusy(true);
    let ok = 0;
    for (const rid of selected) {
      try { await api.post(`/rides/${rid}/auto-assign`); ok += 1; } catch { /* course sans véhicule dispo */ }
    }
    setBulkBusy(false);
    setSelected([]);
    load();
    if (ok) toast.success(`${ok} course(s) affectée(s) au mieux`);
    if (ok < selected.length) toast.warning(`${selected.length - ok} course(s) sans véhicule disponible`);
  };

  const load = () => {
    api.get("/rides", { params: { date } }).then((r) => setRides(r.data));
  };

  // Alerte retards live : patients dont l'attente après fin de soin approche/dépasse le plafond.
  const nowM = () => { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); };
  const [nowMin, setNowMin] = useState(nowM());
  const [maxWait, setMaxWait] = useState(60);
  useEffect(() => {
    const t = setInterval(() => setNowMin(nowM()), 30000);
    api.get("/company/settings").then((r) => setMaxWait(r.data.max_wait_return_min || 60)).catch(() => {});
    return () => clearInterval(t);
  }, []);
  const toMin = (s) => (s ? parseInt(s.slice(0, 2), 10) * 60 + parseInt(s.slice(3, 5), 10) : null);
  const waitAlerts = date === today()
    ? rides
        .filter((r) => r.care_end && ["planned", "en_route"].includes(r.status))
        .map((r) => ({ ...r, wait_now: nowMin - toMin(r.care_end) }))
        .filter((r) => r.wait_now >= Math.max(0, maxWait - 15))
        .sort((a, b) => b.wait_now - a.wait_now)
    : [];

  // Notification sonore : bip dès qu'un patient DÉPASSE le plafond (une seule fois par course).
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("pacemia_wait_sound") !== "off");
  const alertedRef = useRef(new Set());
  const beep = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const tone = (t, f) => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.type = "sine"; o.frequency.value = f;
        g.gain.setValueAtTime(0.35, ctx.currentTime + t);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + 0.35);
        o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + 0.4);
      };
      tone(0, 880); tone(0.45, 880); tone(0.9, 1175);
    } catch { /* audio bloqué par le navigateur avant interaction */ }
  };
  useEffect(() => {
    const fresh = waitAlerts.filter((r) => r.wait_now >= maxWait && !alertedRef.current.has(r.id));
    if (!fresh.length) return;
    fresh.forEach((r) => alertedRef.current.add(r.id));
    if (soundOn) beep();
    toast.error(`Plafond d'attente dépassé : ${fresh.map((r) => `${r.patient_name} (${r.wait_now} min)`).join(", ")}`);
    // eslint-disable-next-line
  }, [waitAlerts, soundOn, maxWait]);
  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem("pacemia_wait_sound", next ? "on" : "off");
    if (next) beep();
  };
  const fetchPos = () => {
    api.get("/fleet/positions", { params: { date } }).then((r) => setPositions(r.data)).catch(() => {});
    api.get("/fleet/driver-positions", { params: { date } }).then((r) => setDriverPos(r.data)).catch(() => {});
  };
  const loadTrack = (did) => {
    setTrackDriver(did);
    if (!did) { setTrack(null); return; }
    api.get("/fleet/driver-track", { params: { driver_id: did, date } })
      .then((r) => setTrack(r.data))
      .catch((err) => { setTrack(null); toast.error(apiError(err)); });
  };
  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data));
    api.get("/drivers").then((r) => setDrivers(r.data));
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  useEffect(() => {
    if (patientPanel) api.get("/patients", { params: { q: pq } }).then((r) => setPatients(r.data)).catch(() => {});
  }, [patientPanel, pq]);

  // Temps réel : statut chauffeur / position GPS -> rechargement instantané.
  useLiveSync((evt) => {
    load();
    if (showMap && evt?.type === "position_updated") {
      fetchPos();
      if (trackDriver) loadTrack(trackDriver);
    }
  });

  // Carte à la demande : UNE seule lecture à l'ouverture (pas de polling permanent).
  // Les mises à jour suivantes arrivent par WebSocket ou via le bouton « Actualiser ».
  useEffect(() => {
    if (showMap) fetchPos();
    if (trackDriver) loadTrack(trackDriver);
    /* eslint-disable-next-line */
  }, [showMap, date]);
  useEffect(() => () => refreshTimers.current.forEach(clearTimeout), []);

  // Géolocalisation à la demande : le serveur interroge les smartphones des chauffeurs
  // (signal WebSocket), qui renvoient leur GPS brut. Marqueurs mis à jour en JS,
  // sans rechargement et sans aucun appel de Geocoding.
  const refreshPositions = async () => {
    setRefreshing(true);
    try {
      await api.post("/tracking/request-refresh");
      toast.message("Interrogation des smartphones des chauffeurs…", {
        description: "Les marqueurs se mettent à jour dès réception du GPS (aucun appel Google).",
      });
    } catch (err) {
      toast.error(apiError(err));
      setRefreshing(false);
      return;
    }
    refreshTimers.current.forEach(clearTimeout);
    refreshTimers.current = [
      setTimeout(fetchPos, 4000),
      setTimeout(fetchPos, 10000),
      setTimeout(() => { fetchPos(); setRefreshing(false); }, 18000),
    ];
  };

  const dragPayload = (e) => {
    try { return JSON.parse(e.dataTransfer.getData("application/json")); } catch { return null; }
  };
  const startRideDrag = (r) => (e) => {
    e.dataTransfer.setData("application/json", JSON.stringify({ kind: "ride", id: r.id, fromVid: r.assigned_vehicle_id || null }));
    e.dataTransfer.effectAllowed = "move";
    setDragging(true);
  };
  const endDrag = () => { setDragging(false); setHoverZone(null); };

  const onDropZone = async (e, vid) => {
    e.preventDefault();
    setHoverZone(null);
    setDragging(false);
    const payload = dragPayload(e);
    if (!payload) return;
    if (payload.kind === "ride") {
      if ((payload.fromVid || null) === (vid || null)) return;
      try {
        const { data } = await api.put(`/rides/${payload.id}/assign`, { vehicle_id: vid });
        if (data.warning) toast.warning(data.warning);
        else toast.success(vid ? "Course réaffectée" : "Course désaffectée");
        load();
      } catch (err) { toast.error(apiError(err)); }
    } else if (payload.kind === "patient") {
      const p = payload.patient;
      const v = vid ? vehicles.find((x) => x.id === vid) : null;
      setCreateDlg({ patient: p, vehicleId: vid || null, vehicleLabel: v ? `${v.type} · ${v.plaque}` : null });
      setNTime("");
      setDestQuery("");
      setDestAddr(null);
      const vts = ["Ambulance", "VSL", "Taxi", "TPMR"];
      setNVt(vts.includes(p.default_vehicle_type) ? p.default_vehicle_type : (v?.type && vts.includes(v.type) ? v.type : "VSL"));
    }
  };
  const zoneProps = (vid) => ({
    onDragOver: (e) => { e.preventDefault(); setHoverZone(vid || "unassigned"); },
    onDragLeave: () => setHoverZone((z) => (z === (vid || "unassigned") ? null : z)),
    onDrop: (e) => onDropZone(e, vid),
  });

  // Glisser-déposer disponible aussi dans la vue « Par chauffeur » : la course part
  // sur le véhicule que ce chauffeur utilise ce jour-là.
  const driverVehicleId = (did) => (byDriverId(did).find((r) => r.assigned_vehicle_id) || {}).assigned_vehicle_id || null;
  const driverZoneProps = (did) => ({
    onDragOver: (e) => { e.preventDefault(); setHoverZone(`drv-${did}`); },
    onDragLeave: () => setHoverZone((z) => (z === `drv-${did}` ? null : z)),
    onDrop: (e) => {
      const vid = driverVehicleId(did);
      if (!vid) {
        e.preventDefault();
        setHoverZone(null); setDragging(false);
        toast.warning("Ce chauffeur n'a aucun véhicule ce jour-là — utilisez « Affecter » ou la vue par véhicule.");
        return;
      }
      onDropZone(e, vid);
    },
  });

  const createFromDrop = async () => {
    const p = createDlg.patient;
    setCreating(true);
    try {
      const { data: ride } = await api.post("/rides", {
        date, time: nTime, vehicle_type: nVt, type: nVt === "Taxi" ? "prive" : "sanitaire",
        patient_id: p.id, patient_name: p.name, patient_phone: p.phone || "",
        pickup_address: p.address || "Domicile patient", pickup_lat: p.lat, pickup_lon: p.lon,
        dropoff_address: destAddr.address, dropoff_lat: destAddr.lat, dropoff_lon: destAddr.lon,
      });
      if (createDlg.vehicleId) {
        const { data } = await api.put(`/rides/${ride.id}/assign`, { vehicle_id: createDlg.vehicleId });
        if (data.warning) toast.warning(data.warning);
      }
      toast.success(`Course créée${createDlg.vehicleId ? " et affectée" : ""} — ${p.name} à ${nTime}`);
      setCreateDlg(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setCreating(false); }
  };

  const viewSignature = async (docId) => {
    try {
      const { data } = await api.get(`/documents/${docId}/url`);
      window.open(`${API_BASE}/documents/download?token=${data.token}`, "_blank");
    } catch (err) { toast.error(apiError(err)); }
  };

  const advance = async (ride) => {
    const idx = STATUS_FLOW.indexOf(ride.status);
    if (idx >= STATUS_FLOW.length - 1) return;
    await api.put(`/rides/${ride.id}/status`, { status: STATUS_FLOW[idx + 1] });
    load();
  };

  const byVehicle = (vid) => rides.filter((r) => r.assigned_vehicle_id === vid).sort((a, b) => a.time.localeCompare(b.time));
  const byDriverId = (did) => rides.filter((r) => (r.assigned_driver_ids || []).includes(did)).sort((a, b) => a.time.localeCompare(b.time));
  const timeline = [...rides].sort((a, b) => (a.time || "").localeCompare(b.time || ""));
  const unassigned = rides.filter((r) => !r.assigned_vehicle_id);
  const absentDrivers = drivers.filter((d) => d.status !== "disponible");
  const driverId = (name) => drivers.find((d) => `${d.prenom} ${d.nom}` === name)?.id;
  const DriverNames = ({ names }) => (names || []).map((n, i) => (
    <React.Fragment key={n}>
      {i > 0 && ", "}
      <EntityLink type="driver" id={driverId(n)} testid={`laregul-driver-link-${driverId(n) || n}`}>{n}</EntityLink>
    </React.Fragment>
  ));

  const RideCard = ({ r, showVehicle, draggable }) => (
    <div
      className={`rounded-md p-2.5 text-xs ${STATUS_META[r.status]?.cls}${draggable ? " cursor-grab active:cursor-grabbing" : ""}${selected.includes(r.id) ? " ring-2 ring-primary" : ""}`}
      data-testid="laregul-ride-card"
      draggable={!!draggable}
      onDragStart={draggable ? startRideDrag(r) : undefined}
      onDragEnd={draggable ? endDrag : undefined}
    >
      <div className="flex items-center justify-between font-semibold">
        <span className="flex items-center gap-1.5 min-w-0">
          <input
            type="checkbox"
            className="no-print shrink-0 cursor-pointer"
            checked={selected.includes(r.id)}
            onChange={() => toggleSelect(r.id)}
            onClick={(e) => e.stopPropagation()}
            title="Sélectionner pour une affectation en lot"
            data-testid={`laregul-select-${r.id}`}
          />
          <span data-testid={`laregul-ride-times-${r.id}`} className="truncate">
            {r.pec_time ? (<><span className="text-primary">{r.pec_time}</span><span className="opacity-60"> → </span></>) : null}
            {r.time} · {r.patient_name}
          </span>
        </span>
        <span className="shrink-0">{r.distance_km} km</span>
      </div>
      {r.shared && <span className="inline-block mt-0.5 text-[10px] font-semibold text-primary bg-primary/10 rounded px-1.5 py-0.5">Groupé ×{r.group_size}</span>}
      {r.care_end && (
        <span className="inline-block mt-0.5 ml-1 text-[10px] font-semibold text-amber-700 bg-amber-100 rounded px-1.5 py-0.5" data-testid={`laregul-return-badge-${r.id}`}>
          Retour · fin de soin {r.care_end}{r.wait_min > 0 ? ` · attente ${r.wait_min} min` : ""}
        </span>
      )}
      {showVehicle && r.assigned_vehicle_id && (
        <p className="mt-0.5 opacity-80">🚐 <EntityLink type="vehicle" id={r.assigned_vehicle_id} testid={`laregul-tl-vehicle-${r.id}`}>{r.assigned_vehicle_label}</EntityLink></p>
      )}
      <p className="mt-1 opacity-80">
        {r.pec_time && <span className="font-semibold tabular-nums mr-1">{r.pec_time}</span>}
        {r.pickup_address}
      </p>
      <p className="opacity-80 flex items-center gap-1">
        <ChevronRight className="h-3 w-3" />
        <span className="font-semibold tabular-nums mr-1">{r.time}</span>
        {r.dropoff_address}
      </p>
      {r.patient_phone && <p className="opacity-70 flex items-center gap-1 mt-0.5"><Phone className="h-3 w-3" />{r.patient_phone}</p>}
      {r.driver_start_time && (
        <p className={`mt-0.5 text-[11px] font-semibold rounded px-1.5 py-0.5 inline-block ${r.early_departure ? "text-red-700 bg-red-100" : "text-amber-700 bg-amber-100"}`} data-testid={`laregul-depart-${r.id}`}>
          {r.early_departure ? "⚠️ " : "🚦 "}Départ dépôt {r.driver_start_time}{r.approach_min ? ` · ${r.approach_min} min de route` : ""}{r.early_departure ? " (avant le début de service)" : ""}
        </p>
      )}
      {r.delayed && (
        <p className="mt-0.5 text-[11px] font-semibold rounded px-1.5 py-0.5 inline-block text-red-800 bg-red-100 border border-red-300" data-testid={`laregul-delayed-${r.id}`}>
          ⚠️ En retard +{r.delay_min} min{r.delay_source === "driver" ? " (signalé)" : ""}{r.delay_reason ? ` — ${r.delay_reason}` : ""}
        </p>
      )}
      {r.needs_reassign && (
        <p className="mt-0.5 text-[11px] font-semibold rounded px-1.5 py-0.5 inline-block text-orange-800 bg-orange-100 border border-orange-300" data-testid={`laregul-reassign-${r.id}`}>
          À réassigner · arrivée estimée {r.expected_pickup_time || "?"}
        </p>
      )}
      {r.over_amplitude && (
        <p className="mt-0.5 text-[11px] font-semibold rounded px-1.5 py-0.5 inline-block text-red-900 bg-red-200 border border-red-400" data-testid={`laregul-over-amp-${r.id}`}>
          ⛔ Amplitude dépassée
        </p>
      )}
      {r.assigned_driver_names?.length > 0 && <p className="opacity-70 mt-0.5">👤 <DriverNames names={r.assigned_driver_names} /></p>}
      {r.signature_status === "signed" && (
        <button
          type="button"
          onClick={() => viewSignature(r.signature_doc_id)}
          className="no-print mt-1 inline-flex items-center gap-1 text-[10px] font-medium text-green-700 dark:text-green-500 hover:underline"
          data-testid={`laregul-signature-${r.id}`}
        >
          <PenLine className="h-3 w-3" /> PMTi signée — voir
        </button>
      )}
      <div className="no-print mt-2 flex items-center justify-between gap-2">
        <Badge variant="outline" className="text-[10px] bg-background/50">{STATUS_META[r.status]?.label}</Badge>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="secondary" className="h-6 px-2 text-[10px] gap-1" onClick={() => openSheet([r.id])} data-testid={`laregul-assign-${r.id}`}>
            <Truck className="h-3 w-3" /> Affecter
          </Button>
          {r.status !== "finished" && (
            <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={() => advance(r)} data-testid={`ride-advance-${r.id}`}>
              {STATUS_META[STATUS_FLOW[STATUS_FLOW.indexOf(r.status) + 1]]?.label} →
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3 no-print">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Planning</h1>
          <p className="text-muted-foreground text-sm">Feuilles de route par véhicule, statuts en temps réel.</p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <label className="text-xs text-muted-foreground">Journée</label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 w-44" data-testid="laregul-date" />
          </div>
          <ChatPanel rides={rides} />
          <Button variant={patientPanel ? "default" : "outline"} onClick={() => setPatientPanel((s) => !s)} data-testid="laregul-dnd-patients" className="gap-2">
            <UserPlus className="h-4 w-4" /> Créer par glisser-déposer
          </Button>
          <Button variant={showMap ? "default" : "outline"} onClick={() => setShowMap((s) => !s)} data-testid="laregul-toggle-map" className="gap-2">
            <Radar className="h-4 w-4" /> Tour de contrôle
          </Button>
          <Button variant="outline" onClick={recomputeGeo} disabled={geoBusy} data-testid="laregul-recompute-geo" className="gap-2">
            <Ruler className={`h-4 w-4 ${geoBusy ? "animate-pulse" : ""}`} /> {geoBusy ? "Recalcul…" : "Recalculer les distances"}
          </Button>
          <Button onClick={() => window.print()} data-testid="laregul-print-pdf" className="gap-2">
            <Printer className="h-4 w-4" /> Imprimer le planning
          </Button>
        </div>
      </div>

      <div className="no-print flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 rounded-md border border-border p-0.5 w-fit" data-testid="laregul-view-switch">
          {[
            { k: "vehicle", label: "Par véhicule", icon: Truck },
            { k: "driver", label: "Par chauffeur", icon: Users },
            { k: "timeline", label: "Chronologique", icon: Clock },
          ].map((v) => (
            <button
              key={v.k}
              type="button"
              onClick={() => setView(v.k)}
              className={`flex items-center gap-1.5 text-sm rounded px-3 py-1.5 font-medium transition-colors ${view === v.k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}
              data-testid={`laregul-view-${v.k}`}
            >
              <v.icon className="h-4 w-4" /> {v.label}
            </button>
          ))}
        </div>
        {view !== "timeline" && (
          <>
            <Button variant="outline" size="sm" className="h-8 gap-1.5"
              onClick={() => collapseAll(view === "vehicle" ? vehicles.map((v) => v.id) : drivers.map((d) => `drv-${d.id}`))}
              data-testid="laregul-collapse-all">
              <ChevronsDownUp className="h-3.5 w-3.5" /> Tout replier
            </Button>
            <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => collapseAll([])} data-testid="laregul-expand-all">
              <ChevronsUpDown className="h-3.5 w-3.5" /> Tout déplier
            </Button>
          </>
        )}
        <span className="text-xs text-muted-foreground">
          Cochez des courses pour une affectation en lot, ou cliquez sur « Affecter » pour choisir un véhicule classé.
        </span>
      </div>

      {dragging && view !== "vehicle" && (
        <div className="no-print sticky top-2 z-30 flex flex-wrap gap-1.5 rounded-md border border-primary/40 bg-card/95 backdrop-blur p-2 shadow-lg" data-testid="laregul-drop-bar">
          <span className="text-xs font-semibold text-primary self-center mr-1">Déposez sur un véhicule :</span>
          {vehicles.filter((v) => v.active).map((v) => (
            <span
              key={v.id}
              className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${hoverZone === v.id ? "border-primary bg-primary text-primary-foreground" : "border-border bg-secondary/60"}`}
              data-testid={`laregul-dropchip-${v.id}`}
              {...zoneProps(v.id)}
            >
              {v.type} · {v.plaque}
            </span>
          ))}
          <span
            className={`text-xs rounded-full border px-2.5 py-1 ${hoverZone === "unassigned" ? "border-destructive bg-destructive text-white" : "border-destructive/50 text-destructive"}`}
            data-testid="laregul-dropchip-unassigned"
            {...zoneProps(null)}
          >
            Désaffecter
          </span>
        </div>
      )}

      {waitAlerts.length > 0 && (
        <Card className="no-print p-3 rounded-md shadow-none border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-900 space-y-1.5" data-testid="laregul-wait-alerts">
          <div className="flex items-center gap-2 font-semibold text-sm text-red-700 dark:text-red-300">
            <AlertTriangle className="h-4 w-4" />
            Attente après fin de soin — {waitAlerts.length} patient(s) à surveiller (plafond {maxWait} min)
            <button type="button" onClick={toggleSound}
              className="ml-auto text-red-700 dark:text-red-300 hover:opacity-70"
              title={soundOn ? "Couper l'alerte sonore" : "Activer l'alerte sonore"}
              data-testid="laregul-wait-sound-toggle">
              {soundOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            </button>
          </div>
          {waitAlerts.map((r) => (
            <div key={r.id} className="text-sm flex flex-wrap items-center gap-2" data-testid={`laregul-wait-alert-${r.id}`}>
              <Badge className={r.wait_now >= maxWait ? "bg-red-600 text-white hover:bg-red-600" : "bg-amber-500 text-white hover:bg-amber-500"}>
                {r.wait_now >= maxWait ? `${r.wait_now} min — PLAFOND DÉPASSÉ` : `${r.wait_now} min d'attente`}
              </Badge>
              <span className="font-medium">{r.patient_name}</span>
              <span className="text-xs text-muted-foreground">
                fin de soin {r.care_end} · {r.assigned_vehicle_label || "non affectée"} · {r.pickup_address}
              </span>
            </div>
          ))}
        </Card>
      )}

      {showMap && (
        <div className="no-print space-y-2" data-testid="laregul-control-tower">
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-slate-500" /> Au dépôt</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-sky-500" /> En route</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-green-600" /> Pris en charge</span>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={showDrivers} onChange={(e) => setShowDrivers(e.target.checked)} data-testid="laregul-toggle-drivers" />
              Chauffeurs ({driverPos.length})
            </label>
            <select
              value={trackDriver}
              onChange={(e) => loadTrack(e.target.value)}
              className="h-8 rounded-md border border-input bg-background px-2 text-xs"
              data-testid="laregul-track-select"
            >
              <option value="">Trajet du jour : aucun</option>
              {drivers.map((d) => (
                <option key={d.id} value={d.id}>{`${d.prenom || ""} ${d.nom || ""}`.trim()}</option>
              ))}
            </select>
            {track && (
              <span className="font-semibold text-foreground" data-testid="laregul-track-info">
                {track.points.length > 1
                  ? `${track.name} · ${track.points.length} points · ${track.distance_km} km`
                  : `${track.name} · aucun trajet enregistré aujourd'hui`}
              </span>
            )}
            <Button size="sm" variant="outline" onClick={refreshPositions} disabled={refreshing} data-testid="laregul-refresh-gps" className="gap-2 ml-auto h-8">
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Interrogation…" : "Actualiser les positions"}
            </Button>
          </div>
          <FleetMap positions={positions} drivers={driverPos} showDrivers={showDrivers} track={track} />
        </div>
      )}

      {patientPanel && (
        <Card className="no-print p-3 rounded-md shadow-none space-y-2 border-primary/30" data-testid="laregul-patient-tray">
          <div className="flex flex-wrap items-center gap-2">
            <Input value={pq} onChange={(e) => setPq(e.target.value)} placeholder="Rechercher un patient…" className="h-8 w-64" data-testid="laregul-patient-search" />
            <span className="text-xs text-muted-foreground">
              Glissez un patient sur une colonne véhicule (vue « Par véhicule ») pour créer une course affectée, ou sur « Non affectées ».
              Glissez aussi une course d'une colonne à l'autre pour la réaffecter.
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5 max-h-28 overflow-auto">
            {patients.map((p) => (
              <span
                key={p.id}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData("application/json", JSON.stringify({
                    kind: "patient",
                    patient: { id: p.id, name: `${p.prenom} ${p.nom}`, phone: p.phone, address: p.address, lat: p.lat, lon: p.lon, default_vehicle_type: p.default_vehicle_type },
                  }));
                  setDragging(true);
                }}
                onDragEnd={endDrag}
                className="inline-flex items-center gap-1 text-xs rounded-full border border-border bg-secondary/60 px-2.5 py-1 cursor-grab active:cursor-grabbing hover:border-primary"
                data-testid={`laregul-patient-chip-${p.id}`}
              >
                <GripVertical className="h-3 w-3 opacity-50" /> {p.prenom} {p.nom}{p.default_vehicle_type ? ` · ${p.default_vehicle_type}` : ""}
              </span>
            ))}
            {patients.length === 0 && <span className="text-xs text-muted-foreground">Aucun patient trouvé.</span>}
          </div>
        </Card>
      )}

      {absentDrivers.length > 0 && (
        <div className="no-print flex flex-wrap gap-2 text-xs">
          {absentDrivers.map((d) => (
            <Badge key={d.id} variant="outline" className="opacity-70">
              {d.prenom} {d.nom} — {d.status === "conges" ? "Congés" : d.status === "maladie" ? "Maladie" : "Formation"}
            </Badge>
          ))}
        </div>
      )}

      <div className="print-area">
        <h2 className="hidden print:block font-display text-xl font-bold mb-3">Planning du {new Date(date).toLocaleDateString("fr-FR")}</h2>

        {view === "vehicle" && (
        <div className="grid gap-4 lg:grid-cols-3 xl:grid-cols-4" data-testid="laregul-board">
          {vehicles.map((v) => {
            const list = byVehicle(v.id);
            const driverNames = [...new Set(list.flatMap((r) => r.assigned_driver_names || []))];
            return (
              <Card
                key={v.id}
                className={`p-3 rounded-md shadow-none transition-shadow ${!v.active ? "opacity-50" : ""} ${hoverZone === v.id ? "ring-2 ring-primary bg-primary/5" : dragging ? "ring-1 ring-primary/30" : ""}`}
                data-testid="laregul-vehicle-column"
                {...zoneProps(v.id)}
              >
                <div className="flex items-center justify-between mb-3 pb-2 border-b border-border">
                  <div>
                    <p className="font-display font-bold text-sm">
                      <EntityLink type="vehicle" id={v.id} testid={`laregul-vehicle-link-${v.id}`}>{v.type}</EntityLink>
                    </p>
                    <p className="text-xs text-muted-foreground">{v.plaque} · {v.depot}</p>
                    <p className="text-xs font-medium mt-0.5 flex items-center gap-1" data-testid="laregul-vehicle-driver">
                      👤 {driverNames.length > 0 ? <DriverNames names={driverNames} /> : <span className="text-muted-foreground font-normal">Aucun chauffeur</span>}
                    </p>
                    {isCollapsed(v.id) && list.length > 0 && (
                      <p className="text-[11px] text-muted-foreground mt-1 tabular-nums" data-testid={`laregul-summary-${v.id}`}>
                        {list[0].pec_time || list[0].time} → {list[list.length - 1].time} · {list.reduce((s, r) => s + (r.distance_km || 0), 0).toFixed(0)} km
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Badge variant="outline" className="text-[10px]">{list.length}</Badge>
                    <button type="button" onClick={() => toggleCollapse(v.id)} className="no-print text-muted-foreground hover:text-foreground" data-testid={`laregul-collapse-${v.id}`}>
                      {isCollapsed(v.id) ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                {!isCollapsed(v.id) && (
                <div className="space-y-2">
                  {list.length === 0 && <p className="text-xs text-muted-foreground py-3 text-center">{dragging ? "Déposez ici" : "Aucune course"}</p>}
                  {list.map((r) => <RideCard key={r.id} r={r} showVehicle={false} draggable />)}
                </div>
                )}
              </Card>
            );
          })}

          {(unassigned.length > 0 || dragging) && (
            <Card
              className={`p-3 rounded-md shadow-none border-destructive/40 transition-shadow ${hoverZone === "unassigned" ? "ring-2 ring-destructive bg-destructive/5" : ""}`}
              data-testid="laregul-unassigned"
              {...zoneProps(null)}
            >
              <p className="font-display font-bold text-sm text-destructive mb-3 pb-2 border-b border-border">Non affectées ({unassigned.length})</p>
              <div className="space-y-2">
                {unassigned.length === 0 && <p className="text-xs text-muted-foreground py-3 text-center">Déposez ici pour désaffecter</p>}
                {unassigned.map((r) => (
                  <div
                    key={r.id}
                    className={`rounded-md p-2.5 text-xs bg-destructive/10 cursor-grab active:cursor-grabbing${selected.includes(r.id) ? " ring-2 ring-primary" : ""}`}
                    draggable
                    onDragStart={startRideDrag(r)}
                    onDragEnd={endDrag}
                    data-testid={`laregul-unassigned-${r.id}`}
                  >
                    <p className="font-semibold flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        className="no-print shrink-0 cursor-pointer"
                        checked={selected.includes(r.id)}
                        onChange={() => toggleSelect(r.id)}
                        data-testid={`laregul-select-${r.id}`}
                      />
                      <span className="truncate">
                        {r.pec_time ? (<><span className="text-primary">{r.pec_time}</span><span className="opacity-60"> → </span></>) : null}
                        {r.time} · {r.patient_name}
                      </span>
                    </p>
                    <p className="opacity-80">{r.pickup_address} → {r.dropoff_address}</p>
                    <div className="no-print mt-1.5 flex gap-1">
                      <Button size="sm" variant="secondary" className="h-6 px-2 text-[10px] gap-1" onClick={() => openSheet([r.id])} data-testid={`laregul-assign-${r.id}`}>
                        <Truck className="h-3 w-3" /> Affecter
                      </Button>
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] gap-1" onClick={() => autoOne(r.id)} data-testid={`laregul-auto-${r.id}`}>
                        <Wand2 className="h-3 w-3" /> Au mieux
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
        )}

        {view === "driver" && (
        <div className="grid gap-4 lg:grid-cols-3 xl:grid-cols-4" data-testid="laregul-board-driver">
          {drivers.map((d) => {
            const list = byDriverId(d.id);
            return (
              <Card
                key={d.id}
                className={`p-3 rounded-md shadow-none transition-shadow ${hoverZone === `drv-${d.id}` ? "ring-2 ring-primary bg-primary/5" : dragging ? "ring-1 ring-primary/30" : ""}`}
                data-testid="laregul-driver-column"
                {...driverZoneProps(d.id)}
              >
                <div className="flex items-center justify-between mb-3 pb-2 border-b border-border">
                  <div>
                    <p className="font-display font-bold text-sm">
                      <EntityLink type="driver" id={d.id} testid={`laregul-dview-link-${d.id}`}>{d.prenom} {d.nom}</EntityLink>
                    </p>
                    <p className="text-xs text-muted-foreground">{d.depot} · {(d.qualifications || []).join(", ") || "—"}</p>
                    {d.status !== "disponible" && <Badge variant="outline" className="text-[10px] mt-0.5 opacity-70">{d.status}</Badge>}
                    {isCollapsed(`drv-${d.id}`) && list.length > 0 && (
                      <p className="text-[11px] text-muted-foreground mt-1 tabular-nums">
                        {list[0].pec_time || list[0].time} → {list[list.length - 1].time} · {list[0].assigned_vehicle_label || ""}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Badge variant="outline" className="text-[10px]">{list.length}</Badge>
                    <button type="button" onClick={() => toggleCollapse(`drv-${d.id}`)} className="no-print text-muted-foreground hover:text-foreground" data-testid={`laregul-collapse-drv-${d.id}`}>
                      {isCollapsed(`drv-${d.id}`) ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                {!isCollapsed(`drv-${d.id}`) && (
                <div className="space-y-2">
                  {list.length === 0 && <p className="text-xs text-muted-foreground py-3 text-center">{dragging ? "Déposez ici" : "Aucune course"}</p>}
                  {list.map((r) => <RideCard key={r.id} r={r} showVehicle draggable />)}
                </div>
                )}
              </Card>
            );
          })}
        </div>
        )}

        {view === "timeline" && (
        <Card className="p-3 rounded-md shadow-none" data-testid="laregul-board-timeline">
          {timeline.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">Aucune course ce jour.</p>}
          <div className="space-y-2">
            {timeline.map((r) => (
              <div key={r.id} className="flex gap-3 items-stretch" data-testid={`laregul-timeline-row-${r.id}`}>
                <div className="w-14 shrink-0 text-right leading-tight">
                  {r.pec_time && <div className="text-[10px] text-primary tabular-nums" data-testid={`laregul-tl-pec-${r.id}`}>PEC {r.pec_time}</div>}
                  <span className="font-display font-bold text-sm tabular-nums">{r.time}</span>
                </div>
                <div className={`w-1 shrink-0 rounded-full ${r.assigned_vehicle_id ? "bg-primary/60" : "bg-destructive/60"}`} />
                <div className="flex-1 min-w-0">
                  <RideCard r={r} showVehicle draggable />
                </div>
              </div>
            ))}
          </div>
        </Card>
        )}
      </div>

      {selected.length > 0 && (
        <div className="no-print fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex flex-wrap items-center gap-2 rounded-full border border-border bg-card/95 backdrop-blur px-3 py-2 shadow-2xl" data-testid="laregul-selection-bar">
          <ListChecks className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold" data-testid="laregul-selection-count">{selected.length} course(s) sélectionnée(s)</span>
          <Button size="sm" className="gap-1.5 rounded-full" onClick={() => openSheet(selected)} data-testid="laregul-bulk-assign">
            <Truck className="h-3.5 w-3.5" /> Affecter à…
          </Button>
          <Button size="sm" variant="secondary" className="gap-1.5 rounded-full" onClick={bulkAuto} disabled={bulkBusy} data-testid="laregul-bulk-auto">
            <Wand2 className="h-3.5 w-3.5" /> Affecter au mieux
          </Button>
          <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setSelected([])} data-testid="laregul-selection-clear">
            Annuler
          </Button>
        </div>
      )}

      <AssignSheet
        open={!!sheet}
        rideIds={sheet?.rideIds || []}
        refRideId={sheet?.refRideId}
        onClose={() => setSheet(null)}
        onDone={() => { load(); setSelected([]); }}
      />

      <Dialog open={!!createDlg} onOpenChange={(o) => !o && setCreateDlg(null)}>
        <DialogContent className="max-w-md" data-testid="laregul-dnd-create-dialog">
          <DialogHeader>
            <DialogTitle>Nouvelle course — {createDlg?.patient?.name}</DialogTitle>
            <DialogDescription className="text-xs">
              {createDlg?.vehicleId ? `Sera affectée à ${createDlg.vehicleLabel}` : "Créée sans affectation (bac « Non affectées »)"} · Départ : {createDlg?.patient?.address || "adresse du patient"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Heure du RDV</Label>
                <Input type="time" value={nTime} onChange={(e) => setNTime(e.target.value)} className="mt-1" data-testid="laregul-dnd-time" />
              </div>
              <div>
                <Label className="text-xs">Catégorie véhicule</Label>
                <Select value={nVt} onValueChange={setNVt}>
                  <SelectTrigger className="mt-1" data-testid="laregul-dnd-vt"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Ambulance", "VSL", "Taxi", "TPMR"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Destination</Label>
              <div className="mt-1">
                <AddressAutocomplete
                  value={destQuery}
                  onChange={(t) => { setDestQuery(t); setDestAddr(null); }}
                  onSelect={(r) => { setDestAddr({ address: r.label, lat: r.lat, lon: r.lon }); setDestQuery(r.label); }}
                  placeholder="Adresse ou établissement de destination"
                  testid="laregul-dnd-dest"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDlg(null)}>Annuler</Button>
            <Button onClick={createFromDrop} disabled={creating || !nTime || !destAddr} data-testid="laregul-dnd-create">
              {creating ? "Création…" : "Créer la course"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
